import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Flex, Text } from '@modusign/design-system';
import isEqual from 'lodash/isEqual';
import { useCallback, useState } from 'react';

import { useSelectionValues } from '@hooks';

import {
  ResourceModal,
  ResourceModalBody,
  ResourceModalFooter,
  ResourceModalHeader,
} from '@app/components/Modal/ResourceModal';
import { ResourceRoleSubject } from '@app/features/authorization';
import AddAssigneeForm from '@app/features/authorization/components/PermissionAssigneeListModal/AddAssigneeForm';
import {
  MAX_COUNT_OF_ADDITIONAL_SHARINGS_PER_RESOURCE,
  MAX_COUNT_OF_AUTOMATIC_SHARINGS_PER_MEMBER,
} from '@app/features/workspaceManagement/constants';

interface GrantResourceSharingTargetModalProps {
  initialState: ResourceRoleSubject[] | undefined;
  maxCountOfSelection?: number;
  isOpen: boolean;
  mode: 'automatic' | 'additional';
  nonAssignables: ResourceRoleSubject[] | undefined;
  preGrantedResourceRoles?: ResourceRoleSubject[];
  subtitle: string;
  title?: string;
  onClose: () => void;
  onConfirm: (targets: ResourceRoleSubject[]) => void | Promise<unknown>;
}

export default function GrantResourceSharingTargetModal({
  initialState,
  maxCountOfSelection: maxCountOfSelectionProp,
  title,
  subtitle,
  isOpen,
  mode,
  nonAssignables,
  preGrantedResourceRoles,
  onClose,
  onConfirm,
}: GrantResourceSharingTargetModalProps) {
  const selectedSubjects = useSelectionValues<ResourceRoleSubject>(initialState);
  // ResourceRoleSelectList의 onChange callback으로 넘겨주기 위해 새롭게 정의합니다.
  const onSubjectToggle = useCallback(
    (values: ResourceRoleSubject[]) => {
      selectedSubjects.clear();
      selectedSubjects.set(values, true);
    },
    [selectedSubjects]
  );
  const maxCountOfSelection =
    maxCountOfSelectionProp ??
    (mode === 'additional'
      ? MAX_COUNT_OF_ADDITIONAL_SHARINGS_PER_RESOURCE
      : MAX_COUNT_OF_AUTOMATIC_SHARINGS_PER_MEMBER);

  const [isLoading, setIsLoading] = useState(false);

  return (
    <ResourceModal isOpen={isOpen} maxWidth="sm">
      <ResourceModalHeader
        resourceName={subtitle}
        title={title ?? (mode === 'additional' ? '추가 공유' : '문서 자동 공유')}
        onClose={onClose}
      />

      <ResourceModalBody>
        <AddAssigneeForm
          disabled={selectedSubjects.count >= maxCountOfSelection}
          nonAssignables={nonAssignables}
          preGrantedResourceRoles={preGrantedResourceRoles}
          subjects={selectedSubjects.selected}
          tooltipContent={`더 이상 ${
            mode === 'additional' ? '추가' : '자동'
          } 공유를 설정할 수 없습니다. (최대 ${maxCountOfSelection}개)`}
          onChange={onSubjectToggle}
        />
      </ResourceModalBody>

      <ResourceModalFooter alignItems="center" justifyContent="end">
        {selectedSubjects.selected.some(
          (rr) => rr.subjectType === 'GROUP' && rr.groupType === 'ALL_MEMBERS'
        ) && (
          <Flex flex="auto" sx={{ gap: 1 }}>
            <Text color="textPrimaryDark">
              <FontAwesomeIcon icon={faExclamationCircle} style={{ marginRight: 3 }} />
              <Text fontSize="14px" fontWeight="medium">
                주의
              </Text>
            </Text>
            <Text>모든 멤버에게 공유됩니다.</Text>
          </Flex>
        )}

        <Button disabled={isLoading} weight="light" onClick={onClose}>
          취소
        </Button>
        <Button
          disabled={isEqual(initialState, selectedSubjects.selected)}
          isLoading={isLoading}
          ml={3}
          variant="primary"
          weight="light"
          onClick={async () => {
            try {
              setIsLoading(true);

              await onConfirm(
                selectedSubjects.selected.map((s) => {
                  return s.subjectType === 'MEMBER'
                    ? {
                        subjectType: s.subjectType,
                        subjectId: s.subjectId,
                        memberName: s.memberName,
                        memberEmail: s.memberEmail,
                      }
                    : {
                        subjectType: s.subjectType,
                        subjectId: s.subjectId,
                        groupName: s.groupName,
                        groupType: s.groupType,
                      };
                })
              );
            } finally {
              setIsLoading(false);
            }
          }}
        >
          적용
        </Button>
      </ResourceModalFooter>
    </ResourceModal>
  );
}
